<script setup>
/*
 * Relaterede varer og artikler i produktsidens sidebar.
 *
 * Afløser to hårdkodede pladsholdere der stod med teksten "Box 1" på hver
 * eneste produktside. De blev lagt ind sammen med ApexCharts-integrationen og
 * aldrig fyldt.
 *
 * Erstatter samtidig de to tekstlister nederst på siden. De var der for
 * link-flowet — 77.228 produktsider havde nul udgående links til andre varer —
 * så antallet er holdt oppe her: seks varer og tre artikler, alle rigtige
 * links, renderet server-side så crawlere ser dem.
 *
 * SSR-sikker: ingen window, ingen onMounted. Billeder skjules hvis de fejler,
 * i stedet for at vise browserens brudte-billede-ikon.
 */
import { produktnavn } from '@/Composables/produktnavn';
import { Link } from '@inertiajs/vue3';
import { ref } from 'vue';

defineProps({
    varer: { type: Array, default: () => [] },
    artikler: { type: Array, default: () => [] },
});

// Ét sæt fejlede id'er for begge lister. En vare og en artikel kan ikke have
// samme nøgle, fordi de præfikses.
const fejlede = ref(new Set());
const skjul = (noegle) => fejlede.value.add(noegle);
const virker = (noegle) => !fejlede.value.has(noegle);

const prisTekst = (v) => v.lowest_price
    ? `${Number(v.lowest_price).toLocaleString('da-DK', { maximumFractionDigits: 0 })} kr.`
    : null;
</script>

<template>
    <div class="sidebar-relaterede">
        <section v-if="varer.length" class="card mb-3">
            <div class="card-body p-3">
                <h2 class="h6 mb-3">Relaterede varer</h2>
                <ul class="list-unstyled mb-0">
                    <li v-for="v in varer" :key="v.id" class="mb-3">
                        <Link :href="'/produkt/' + v.slug" class="d-flex gap-2 align-items-center text-decoration-none rel-link">
                            <span class="rel-thumb flex-shrink-0">
                                <picture v-if="v.image_id && virker('v' + v.id)">
                                    <source :srcset="`/images/${v.image_id}?w=96&fmt=webp`" type="image/webp" />
                                    <img
                                        :src="`/images/${v.image_id}?w=96`"
                                        :alt="produktnavn(v)"
                                        width="48"
                                        height="48"
                                        loading="lazy"
                                        decoding="async"
                                        @error="skjul('v' + v.id)"
                                    >
                                </picture>
                            </span>
                            <span class="rel-tekst">
                                <span class="d-block rel-navn">{{ produktnavn(v) }}</span>
                                <span v-if="prisTekst(v)" class="d-block rel-pris">{{ prisTekst(v) }}</span>
                            </span>
                        </Link>
                    </li>
                </ul>
            </div>
        </section>

        <section v-if="artikler.length" class="card mb-3">
            <div class="card-body p-3">
                <h2 class="h6 mb-3">Relaterede artikler</h2>
                <ul class="list-unstyled mb-0">
                    <li v-for="a in artikler" :key="a.id" class="mb-3">
                        <Link :href="'/artikel/' + a.slug" class="d-flex gap-2 align-items-center text-decoration-none rel-link">
                            <span class="rel-thumb flex-shrink-0">
                                <img
                                    v-if="a.cover_image && virker('a' + a.id)"
                                    :src="route('getArticleImage', { path: a.cover_image, w: 96, fmt: 'webp' })"
                                    :alt="a.navn"
                                    width="48"
                                    height="48"
                                    loading="lazy"
                                    decoding="async"
                                    @error="skjul('a' + a.id)"
                                >
                            </span>
                            <span class="rel-tekst">
                                <span class="d-block rel-navn">{{ a.navn }}</span>
                                <span v-if="a.dato" class="d-block rel-pris">{{ a.dato }}</span>
                            </span>
                        </Link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<style scoped>
.rel-thumb {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    /*
     * Pladsen holdes ÅBEN, også når der ikke er et billede. Ellers hopper
     * teksten vandret mellem rækker med og uden, og listen ser i stykker ud.
     */
    background: var(--pc-bg-tertiary);
    border-radius: var(--pc-radius);
    overflow: hidden;
}

.rel-thumb img {
    width: 48px;
    height: 48px;
    object-fit: contain;
}

.rel-tekst {
    min-width: 0;
}

.rel-navn {
    font-size: 0.8rem;
    line-height: 1.25;
    color: var(--pc-text-primary);
    /* To linjer og så afkortning: varenavne er lange, og sidebaren er smal. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.rel-pris {
    font-size: 0.75rem;
    color: var(--pc-price, var(--pc-accent));
    font-weight: 600;
}

.rel-link:hover .rel-navn {
    color: var(--pc-accent);
}
</style>
